import { StandardEvents } from '@shopify/events';

/**
 * FYD cart unit-price suppressor.
 *
 * `snippets/cart-products.liquid` renders a per-unit price for every cart line inside
 * `.cart-items__unit-price-wrapper` (an unclassed `<div>` holding `item.final_price` /
 * `item.original_price`). For fabric yardage (FYD) that number is the RAW ¼-yard price, which
 * is meaningless to a shopper who buys in yards — the cart PAGE already renders the per-yard
 * figure next to it as `.cart-items__fyd-unit-price`. This module hides the raw ¼-yard price on
 * FYD lines, in both the cart page and the cart drawer, while keeping the per-yard line and the
 * line total (`.cart-items__price`, which is raw-correct and must never be multiplied).
 *
 * Why a generated stylesheet rather than classes on the rows:
 * `component-cart-items.js` re-renders the cart through `morphSection()` on every quantity
 * change, removal and external `cartLinesUpdate`, and `morph.js` only preserves a hard-coded
 * attribute allowlist — any class or attribute we injected on a row or cell would be wiped. A
 * `<style>` element in `<head>` is never touched by the morph, so rules keyed on the
 * server-rendered `data-key` keep applying (including to rows the morph has just introduced).
 *
 * How FYD lines are identified:
 * - Cart PAGE: from markup alone — FYD rows contain `<fractional-cart-quantity-selector-component>`
 *   (see `snippets/quantity-selector.liquid`, gated on `fyd_page`). This rule is emitted
 *   synchronously on load, so there is no flash of the ¼-yard price.
 * - Cart DRAWER: the drawer renders the plain selector and the cart markup carries no SKU, so
 *   the FYD lines are resolved from `/cart.js` (`key` → `sku`) and matched on `data-key`. The
 *   same rules backstop the page.
 *
 * FYD detection mirrors `snippets/fyd.liquid`: SKU starts with `settings.fyd_sku_prefix`,
 * handed to JS as `Theme.fyd.sku_prefix` from `snippets/scripts.liquid`.
 *
 * See docs/plans/0004-fractional-quantity.md.
 */

const STYLE_ID = 'fyd-cart-price-styles';
const DEFAULT_SKU_PREFIX = 'FYD';

/**
 * The raw per-unit price div is the only unclassed child of the wrapper; the per-yard line
 * (`.cart-items__fyd-unit-price`) carries a class and is therefore kept.
 */
const RAW_PRICE = '.cart-items__unit-price-wrapper > div:not([class])';

/**
 * In the drawer no per-yard line is rendered, so hiding the raw price leaves an empty wrapper
 * sitting in the row's `unit_price` grid area — collapse it too.
 */
const EMPTY_WRAPPER = '.cart-items__unit-price-wrapper:not(:has(> .cart-items__fyd-unit-price))';

/** Cart page: FYD rows are identifiable without any cart data. */
const STATIC_RULES = `
.cart-items__table-row:has(fractional-cart-quantity-selector-component) ${RAW_PRICE} { display: none; }
`;

/** @type {Promise<void> | null} */
let pendingRefresh = null;

/** @returns {string} the FYD SKU prefix, mirroring `settings.fyd_sku_prefix`. */
function skuPrefix() {
  return Theme?.fyd?.sku_prefix || DEFAULT_SKU_PREFIX;
}

/** @returns {HTMLStyleElement} the style element this module owns, creating it on first use. */
function styleElement() {
  const existing = document.getElementById(STYLE_ID);
  if (existing instanceof HTMLStyleElement) return existing;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STATIC_RULES;
  document.head.appendChild(style);
  return style;
}

/**
 * Escapes a cart line key for use inside a double-quoted attribute selector value.
 *
 * @param {string} key
 * @returns {string}
 */
function escapeKey(key) {
  return key.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * @param {string[]} keys - Cart line keys belonging to FYD lines.
 * @returns {string}
 */
function rulesFor(keys) {
  return keys
    .map((key) => {
      const row = `.cart-items__table-row[data-key="${escapeKey(key)}"]`;
      return `${row} ${RAW_PRICE},\n${row} ${EMPTY_WRAPPER} { display: none; }`;
    })
    .join('\n');
}

/**
 * Fetches the cart and rewrites the generated rules. Fails open — on any error the prices stay
 * as the server rendered them.
 *
 * @returns {Promise<void>}
 */
async function refresh() {
  const cartUrl = Theme?.routes?.cart_url;
  if (!cartUrl) return;

  try {
    const response = await fetch(`${cartUrl}.js`, { cache: 'no-store' });
    if (!response.ok) return;

    const cart = await response.json();
    const prefix = skuPrefix();
    const keys = (cart?.items ?? [])
      .filter((/** @type {{ sku?: string }} */ item) => item?.sku?.startsWith(prefix))
      .map((/** @type {{ key: string }} */ item) => item.key);

    styleElement().textContent = `${STATIC_RULES}${rulesFor(keys)}`;
  } catch (error) {
    console.warn('[fyd-cart-price] Could not resolve FYD cart lines:', error);
  }
}

/**
 * Coalesces overlapping refreshes — several cart events can land in the same tick.
 *
 * @returns {Promise<void>}
 */
function scheduleRefresh() {
  pendingRefresh ??= refresh().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
}

// Emit the static (cart page) rules immediately so the ¼-yard price never paints.
styleElement();

// The cart drawer — and therefore its line rows — is rendered into every page by
// `layout/theme.liquid`, so "no rows anywhere" means an empty cart: nothing to resolve, and no
// reason to spend a request on it. A later `cartLinesUpdate` will trigger the first refresh.
if (document.querySelector('.cart-items__table-row')) scheduleRefresh();

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  // Mirrors `component-cart-items.js`: the cart is only settled once the event's promise
  // resolves, otherwise `/cart.js` can return the pre-update state.
  const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
  if (promise) await promise.catch(() => {});
  scheduleRefresh();
});
